import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import { TypographyKeys } from '@thanx/uikit/theme/typography'
import { buildTranslate } from 'locales'
import React from 'react'

const t = buildTranslate('order_ratings.settings.edit_permission')

type Props = {
  // The live answer from useSettingsEditPermission, already combined with
  // `permissionResolved` by the caller. Passed in, never read here: this file
  // sits on the presentational side of the line and fetches nothing.
  editDisabled: boolean
  // The resolved permission name, interpolated into the body sentence.
  permissionLabel: string
}

// FR-60's TAB-level note: one banner above the whole Settings sub-nav, saying
// why every control under it is inert. Renders nothing for a viewer who can
// edit, so the consumer mounts it unconditionally at the top of the tab.
//
// This is the VISIBLE half of the gate. The per-control PermissionTooltip is
// the other half, and it only surfaces on hover or focus (and only while
// `rbac-permission-tooltips` is on), so it cannot be the only explanation a
// sighted viewer gets.
//
// Warning kind, matching the FR-58 gates on the same tab: both report a state
// that limits what the merchant can do here, so they read as the same class of
// message.
//
// `role="status"`, the same live region components/banners/WarningBanner
// carries: switching merchant via MerchantPicker turns this tab read-only
// without remounting the page, so the banner appears in an existing tree. With
// no live region a screen-reader user is never told the tab just went
// read-only.
const EditPermissionBanner = ({ editDisabled, permissionLabel }: Props) => {
  if (!editDisabled) return null

  return (
    <div className="mb-m" role="status">
      <Notification kind="warning" size="full">
        <Text tag="span" typography={TypographyKeys.BodyText5} color="grey90">
          <Text tag="span" typography={TypographyKeys.BodyText5} bold>
            {t('banner_headline')}
          </Text>{' '}
          {t('banner_body', { label: permissionLabel })}
        </Text>
      </Notification>
    </div>
  )
}

export default EditPermissionBanner
